// src/features/media/components/ClipToolContent.tsx

import { useState, useMemo, useEffect } from 'react';
import {
  Stack,
  Group,
  Text,
  TextInput,
  Select,
  Button,
  Alert,
  AspectRatio,
  Divider,
} from '@mantine/core';
import { IconInfoCircle, IconKeyboard } from '@tabler/icons-react';
import { MediaPlayerLinker } from '@lib2/model/player/PlayerLinker';
import { Hostname } from '@lib2/model/common/ServiceHosts';
import { usePlayers } from '@/features/players/usePlayers';
import { useTranscodeVods } from '../useTranscodeVods';
import { useClipMutation } from '../useClipMutation';
import { usePlayerTime } from '../hooks/usePlayerTime';
import { MediaUtils } from '../MediaUtils';
import { ClipRangeSelector } from './ClipRangeSelector';
import { VIDEO_CONSTRAINTS } from '@/lib/constants/media';
import type { VideoViewData, ClipCreateRequest } from '@/lib/types/Media';
import type IMediaView from '@lib2/model/media/IMediaView';

interface ClipToolContentProps {
  video: VideoViewData;
  onSuccess: (newVideo: VideoViewData) => void;
  onCancel: () => void;
}

export function ClipToolContent({
  video,
  onSuccess,
  onCancel,
}: ClipToolContentProps) {
  const { defaultPlayerId } = usePlayers();
  const { data: transcodeVods, isLoading: vodsLoading } = useTranscodeVods();
  const clipMutation = useClipMutation();
  const { currentTime } = usePlayerTime();

  const duration = video.media.duration;

  // Clip state
  const [startSecond, setStartSecond] = useState(0);
  const [endSecond, setEndSecond] = useState(duration);
  const [title, setTitle] = useState(`Clip of ${video.media.title}`);
  const [selectedVodId, setSelectedVodId] = useState<string | null>(null);

  // Keyboard shortcuts for I/O
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (e.key === 'i' || e.key === 'I') {
        e.preventDefault();
        const newStart = Math.floor(currentTime);
        if (newStart < endSecond) {
          setStartSecond(newStart);
        }
      }
      if (e.key === 'o' || e.key === 'O') {
        e.preventDefault();
        const newEnd = Math.floor(currentTime);
        if (newEnd > startSecond && newEnd <= duration) {
          setEndSecond(newEnd);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTime, startSecond, endSecond, duration]);

  // Compute preview URL
  const previewUrl = useMemo(() => {
    if (!defaultPlayerId) return null;
    const mediaView = { data: video } as unknown as IMediaView;
    const isReseller = Hostname.isResellerHostname();
    const linker = new MediaPlayerLinker(defaultPlayerId, mediaView, isReseller);
    return linker.getPreviewUrl(false, { isPreview: true });
  }, [defaultPlayerId, video]);

  // TranscodeVod options sorted by resolution (highest first)
  const vodOptions = useMemo(() => {
    if (!transcodeVods?.data) return [];
    return transcodeVods.data
      .map((vod) => {
        const maxHeight = Math.max(
          ...vod.outputs.map((o) => o.videoVerticalSize ?? 0)
        );
        return {
          value: vod.transcodeVodId.toString(),
          label: `${maxHeight}p - ${vod.label}`,
          maxHeight,
        };
      })
      .sort((a, b) => b.maxHeight - a.maxHeight);
  }, [transcodeVods]);

  // Auto-select highest quality
  useEffect(() => {
    if (vodOptions.length > 0 && !selectedVodId) {
      setSelectedVodId(vodOptions[0].value);
    }
  }, [vodOptions, selectedVodId]);

  // Validation
  const clipDuration = endSecond - startSecond;
  const isValid =
    title.trim().length >= 1 && clipDuration >= 1 && startSecond < endSecond;

  const handleRangeChange = (start: number, end: number) => {
    setStartSecond(start);
    setEndSecond(end);
  };

  const handleSubmit = () => {
    if (!isValid) return;

    const updateReq = MediaUtils.transformFormToUpdateRequest(
      {
        ...MediaUtils.transformVideoToFormData(video),
        title: title.trim(),
        refId: '', // Clear refId for clips
      },
      false
    );
    updateReq.refId = null; // Ensure null for API

    const req: ClipCreateRequest = {
      start: startSecond,
      end: endSecond,
      updateMediaReq: updateReq,
      transcodeVodId: selectedVodId ? parseInt(selectedVodId, 10) : null,
    };

    clipMutation.mutate({ mediaId: video.media.mediaId, req }, { onSuccess });
  };

  return (
    <Stack gap="lg">
      {/* Source info */}
      <Text size="sm" c="dimmed">
        Source: "{video.media.title}" · {MediaUtils.formatTimecode(duration)}{' '}
        duration
      </Text>

      {/* Player */}
      {previewUrl && (
        <AspectRatio ratio={16 / 9}>
          <iframe
            src={previewUrl}
            style={{ border: 'none', width: '100%', height: '100%' }}
            allowFullScreen
            title="Clip preview"
          />
        </AspectRatio>
      )}

      {/* Range selector with times and Set buttons */}
      <ClipRangeSelector
        duration={duration}
        startSecond={startSecond}
        endSecond={endSecond}
        onChange={handleRangeChange}
        onSetIn={() => {
          const newStart = Math.floor(currentTime);
          if (newStart < endSecond) {
            setStartSecond(newStart);
          }
        }}
        onSetOut={() => {
          const newEnd = Math.floor(currentTime);
          if (newEnd > startSecond && newEnd <= duration) {
            setEndSecond(newEnd);
          }
        }}
      />

      {/* Keyboard shortcut hint */}
      <Group gap="xs">
        <IconKeyboard size={16} color="var(--mantine-color-gray-5)" />
        <Text size="xs" c="dimmed">
          Tip: Press <Text span fw={600}>I</Text> / <Text span fw={600}>O</Text> keys to set points (when player not focused)
        </Text>
      </Group>

      {/* Keyframe notice */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        Clip points may adjust to nearest keyframe
      </Alert>

      <Divider />

      {/* Title */}
      <TextInput
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={VIDEO_CONSTRAINTS.TITLE_MAX}
        description={`${title.length}/${VIDEO_CONSTRAINTS.TITLE_MAX}`}
        required
        error={title.trim().length < 1 ? 'Title is required' : undefined}
      />

      {/* Quality selector */}
      <Select
        label="Output Quality"
        data={vodOptions}
        value={selectedVodId}
        onChange={setSelectedVodId}
        disabled={vodsLoading}
        placeholder={vodsLoading ? 'Loading...' : 'Select quality'}
      />

      {/* Actions */}
      <Group justify="flex-end" mt="md">
        <Button
          variant="default"
          onClick={onCancel}
          disabled={clipMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={clipMutation.isPending}
          disabled={!isValid}
        >
          Create Clip
        </Button>
      </Group>
    </Stack>
  );
}
